import { Spacing } from '@persona-id/design-tokens';
import {
  FocusEventHandler,
  FormEventHandler,
  KeyboardEventHandler,
  memo,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import Input from 'design-system/components/Input';
import { Label } from 'design-system/components/Text';
import { SizingUnitTypes } from 'lib/interfaces/ui-components';

export const StyleLabel = styled(Label).attrs(() => ({ size: 'md', weight: 'bold' }))`
  display: block;
  margin-bottom: ${Spacing[1]};
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  font-weight: ${({ theme }) => theme.text.paragraph.sm.fontWeight};
`;

export const HintText = styled.div<{ hasError?: boolean }>`
  display: block;
  color: ${({ hasError, theme }) => (hasError ? theme.color.variants.danger.active : 'inherit')};
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  font-weight: ${({ theme }) => theme.text.paragraph.sm.fontWeight};
  line-height: ${({ theme }) => theme.text.paragraph.sm.lineHeight};
  text-align: center;
`;

export const StyleInputContainer = styled.div`
  input {
    padding-right: ${Spacing[5]};
  }
`;

export const StyleInputWrapper = styled.div`
  display: flex;
  position: relative;
  align-items: center;
`;

export const StyleInputField = styled(Input)`
  flex-grow: 1;
`;

const INPUT_LIMIT = 1000;

const isNegativeZero = (value: string) => value === '-0';

const StyleUnitContainer = styled.span`
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  color: ${({ theme }) => theme.color.text.secondary};
  position: absolute;
  right: 0;
  margin-right: 8px;
  pointer-events: none;
`;

type StyleInputProps = {
  name: string;
  field: string;
  label?: string;
  submit: (value: string, field: string) => void;
  onChange?: (value: string) => void;
  defaultValue: string;
  allowNegativeNumbers?: boolean;
  editable: boolean;
  valueLimit?: number;
  unitValue?: SizingUnitTypes;
  hintText?: string;
};

const StyleInput = ({
  name,
  label,
  submit,
  defaultValue,
  editable,
  field,
  allowNegativeNumbers,
  onChange,
  unitValue,
  valueLimit,
  hintText,
}: StyleInputProps) => {
  const inputRef: MutableRefObject<HTMLInputElement | null> = useRef(null);
  const [value, setValue] = useState(defaultValue);
  const inputLimit = valueLimit ?? INPUT_LIMIT;

  const isAboveZero = useCallback(
    (value: string) => {
      const inputValue = parseInt(value);
      return (
        !isNegativeZero(value) &&
        !Number.isNaN(inputValue) &&
        (allowNegativeNumbers ? true : inputValue >= 0)
      );
    },
    [allowNegativeNumbers],
  );

  const isBelowLimit = useCallback((value: number) => value <= inputLimit, [inputLimit]);

  const isInputValid = useCallback(
    (value: string) => {
      const inputValue = parseInt(value);
      if (isAboveZero(value) && isBelowLimit(inputValue)) {
        return true;
      }

      return false;
    },
    [isAboveZero, isBelowLimit],
  );

  const clampValue = useCallback(
    (value: string) => {
      const inputValue = parseInt(value);
      if (!isAboveZero(value)) return '0';
      if (!isBelowLimit(inputValue)) return inputLimit.toString();
    },
    [isBelowLimit, isAboveZero, inputLimit],
  );

  const onSubmit = useCallback(
    (value: string, field: string) => {
      if (isInputValid(value)) {
        submit(value, field);
      } else {
        const clampedValue = clampValue(value);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setValue(clampedValue);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        submit(clampedValue, field);
      }
    },
    [isInputValid, submit, clampValue],
  );

  useEffect(() => {
    if (inputRef.current != null && defaultValue !== inputRef.current.value) {
      setValue(defaultValue);
    }
  }, [defaultValue]);

  const handleChange: FormEventHandler<HTMLInputElement> = (e) => {
    const nativeEvent = e.nativeEvent as InputEvent;
    const value = (e.target as HTMLInputElement).value;
    if (nativeEvent.inputType != null) {
      setValue(value);
      onChange?.(value);
    } else {
      onSubmit(value, field);
    }
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    if (defaultValue !== e.target.value) {
      onSubmit(e.target.value, field);
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      onSubmit(e.target.value, field);
      return;
    }
  };

  const hasError = !isInputValid(value);

  return (
    <StyleInputContainer>
      {label && <StyleLabel htmlFor={name}>{label}</StyleLabel>}
      <StyleInputWrapper>
        <StyleInputField
          ref={inputRef}
          name={name}
          id={name}
          placeholder={value}
          hasError={hasError}
          data-has-error={hasError}
          value={value}
          type='number'
          size='sm'
          disabled={!editable}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
        />
        <StyleUnitContainer>{unitValue?.toUpperCase() ?? 'PX'}</StyleUnitContainer>
      </StyleInputWrapper>
      {hintText && <HintText hasError={hasError}>{hintText}</HintText>}
    </StyleInputContainer>
  );
};

export default memo(StyleInput);
